import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './apiConfig';
import type { IUser, IMfg } from 'models';

/** rest_auth.login when email MFA applies: the SPA must leave for the
 * server-rendered /verify-mfa/ page, which completes the login. */
export interface LoginMfaRedirect {
  mfa_required: true;
  redirect: string;
}

export interface ResetStatus {
  valid: boolean;
  username?: string;
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery,
  tagTypes: ['CurrentUser'],
  endpoints: (builder) => ({
    getCurrentUser: builder.query<IUser, void>({
      query: () => '/api/v1.0/users/get_current/',
      providesTags: ['CurrentUser'],
    }),
    getMfg: builder.query<IMfg, number>({
      query: (id) => `/api/v1.0/mfg/${id}`,
    }),
    login: builder.mutation<IUser | LoginMfaRedirect, { username: string; password: string; remember?: boolean }>({
      query: (credentials) => ({
        url: '/api/v1.0/auth/login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['CurrentUser'],
    }),
    logout: builder.mutation<void, void>({
      query: () => ({ url: '/api/v1.0/auth/logout', method: 'POST' }),
      invalidatesTags: ['CurrentUser'],
    }),
    forgotPassword: builder.mutation<{ submitted: boolean }, { email: string }>({
      query: (body) => ({ url: '/api/v1.0/auth/forgot-password', method: 'POST', body }),
    }),
    resetPasswordStatus: builder.query<ResetStatus, void>({
      query: () => '/api/v1.0/auth/reset-password/status',
    }),
    resetPassword: builder.mutation<
      { success: boolean },
      { new_password: string; confirm_password: string }
    >({
      query: (body) => ({ url: '/api/v1.0/auth/reset-password', method: 'POST', body }),
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useGetMfgQuery,
  useLoginMutation,
  useLogoutMutation,
  useForgotPasswordMutation,
  useResetPasswordStatusQuery,
  useResetPasswordMutation,
} = authApi;
